import { useQuery } from "@tanstack/react-query";
import { fetchWithAuth } from "./useAuth";
import { Product } from "@/prisma-types";

export type ProductStatus = "DRAFT" | "ACTIVE" | "ARCHIVED";

export interface ProductVariant {
  id: number;
  title: string;
  sku?: string | null;
  price: number | string; // server may return decimal-like string
  compareAtPrice?: number | string | null;
  costPrice?: number | string | null;
  inventoryQuantity: number;
  inventoryPolicy?: "DENY" | "CONTINUE";
  inventoryTracked?: boolean;
  requiresShipping?: boolean;
  weight?: number | null;
  weightUnit?: string | null;
}

export interface ProductImage {
  id: number;
  url: string;
  altText?: string | null;
  isFeatured?: boolean;
  position?: number;
}

export interface CategoryLite {
  id: number;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}



type ProductsResponse = Product[];

function coerceNumber(
  n: number | string | undefined | null
): number | undefined {
  if (n === undefined || n === null) return undefined;
  const v = typeof n === "string" ? Number(n) : n;
  return Number.isFinite(v) ? (v as number) : undefined;
}

export function useProducts(options?: { enabled?: boolean }) {
  return useQuery<ProductsResponse, Error>({
    queryKey: ["products"],
    enabled: options?.enabled,
    queryFn: async () => {
      const response = await fetchWithAuth(`/api/admin/products`);
      if (!response.ok) throw new Error("Eroare la încărcarea produselor");
      const raw = (await response.json()) as ProductsResponse;
      return (raw || []).map((p) => {
        const firstVariant = (p.variants || [])[0];
        const priceNum = coerceNumber(firstVariant?.price) ?? undefined;
        const stockSum = (p.variants || []).reduce(
          (sum, v) =>
            sum +
            (Number.isFinite(v.inventoryQuantity) ? v.inventoryQuantity : 0),
          0
        );
        const imagesArray = (p.images || []) as ProductImage[];
        return {
          ...p,
          name: p.title ?? p.name, // alias for legacy UI
          price: priceNum ?? p.price,
          stock: Number.isFinite(stockSum) ? stockSum : p.stock,
          hide: p.status ? p.status !== "ACTIVE" : p.hide,
          images: imagesArray,
        } as Product;
      });
    },
  });
}
